import React, { useState, useEffect, useCallback } from 'react';
import http from '../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const API_URL = '/category';

const CategoriesManagement = () => {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await http.get(`${API_URL}/all`);
      setCategories(res.data);
    } catch (err) {
      console.error('Error al cargar categorías:', err);
      toast.error('❌ Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Carga inicial de categorías
    fetchCategories();
  }, [fetchCategories]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      toast.warning('⚠️ El nombre de la categoría no puede estar vacío');
      return;
    }

    try {
      await http.post(`${API_URL}/create`, { name: newCategory });
      toast.success('✅ Categoría creada correctamente');
      setNewCategory('');
      fetchCategories();
    } catch (err) {
      console.error('Error al crear categoría:', err);
      toast.error(err.response?.data?.message || '❌ Error al crear categoría');
    }
  };

  const handleUpdate = async (id) => {
    if (!editingName.trim()) {
      toast.warning('⚠️ El nombre de la categoría no puede estar vacío');
      return;
    }

    try {
      await http.patch(`${API_URL}/update/${id}`, { name: editingName });
      toast.success('✅ Categoría actualizada correctamente');
      setEditingId(null);
      setEditingName('');
      fetchCategories();
    } catch (err) {
      console.error('Error al actualizar categoría:', err);
      toast.error(err.response?.data?.message || '❌ Error al actualizar categoría');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('⚠️ ¿Estás seguro de eliminar esta categoría?\n\nLos productos asociados no se eliminarán.')) {
      return;
    }
    try {
      await http.delete(`${API_URL}/delete/${id}`);
      toast.success('✅ Categoría eliminada correctamente');
      fetchCategories();
    } catch (err) {
      console.error('Error al eliminar categoría:', err);
      toast.error(err.response?.data?.message || '❌ Error al eliminar categoría');
    }
  };

  return (
    <div className="container-safe" style={{ padding: '30px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <h1 style={{ margin: 0 }}>🏷️ Gestión de Categorías</h1>
        <div className="stack-sm" style={{ display: 'flex', gap: '10px' }}>
          <button style={btnNav} onClick={() => navigate('/')}>🏠 Dashboard</button>
          <button style={{ ...btnNav, background: '#28a745' }} onClick={() => navigate('/products')}>📦 Productos</button>
          <button style={{ ...btnNav, background: '#dc3545' }} onClick={() => navigate('/login')}>🚪 Salir</button>
        </div>
      </div>

      {/* Formulario crear categoría */}
      <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '15px' }}>➕ Nueva Categoría</h3>
        <form onSubmit={handleCreate} className="form-grid" style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            placeholder="Nombre de la categoría"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            style={inputStyle}
          />
          <button type="submit" style={btnGreen}>Crear</button>
        </form>
      </div>

      {/* Lista de categorías */}
      {loading ? (
        <p>Cargando categorías...</p>
      ) : categories.length === 0 ? (
        <p>No hay categorías registradas. Crea la primera.</p>
      ) : (
        <>
          {/* Desktop/Tableta: tabla clásica */}
          <div className="responsive-table hide-on-mobile" style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: '15px' }}>📋 Categorías Existentes ({categories.length})</h3>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#007bff', color: 'white' }}>
                  <th style={thStyle}>Nombre</th>
                  <th style={thStyle}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={tdStyle}>
                      {editingId === cat._id ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          style={inputStyle}
                          autoFocus
                        />
                      ) : (
                        cat.category
                      )}
                    </td>
                    <td style={tdStyle}>
                          {editingId === cat._id ? (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button style={btnSmallGreen} onClick={() => handleUpdate(cat._id)}>✓ Guardar</button>
                          <button style={btnSmallGray} onClick={() => { setEditingId(null); setEditingName(''); }}>✗ Cancelar</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            style={btnSmallBlue} 
                            onClick={() => { setEditingId(cat._id); setEditingName(cat.category); }}
                          >
                            ✏️ Editar
                          </button>
                          <button 
                            style={btnSmallRed}
                            onClick={() => handleDelete(cat._id)}
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Móvil: tarjetas */}
          <div className="show-on-mobile" style={{ display: 'grid', gap: '12px' }}>
            {categories.map((cat) => (
              <div key={cat._id} style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', padding: '12px' }}>
                {editingId === cat._id ? (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      style={inputStyle}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button style={{ ...btnSmallGreen, flex: 1 }} onClick={() => handleUpdate(cat._id)}>✓ Guardar</button>
                      <button style={{ ...btnSmallGray, flex: 1 }} onClick={() => { setEditingId(null); setEditingName(''); }}>✗ Cancelar</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <div style={{ fontWeight: 600 }}>{cat.category}</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        style={btnSmallBlue} 
                        onClick={() => { setEditingId(cat._id); setEditingName(cat.category); }}
                        aria-label={`Editar ${cat.category}`}
                      >
                        ✏️
                      </button>
                      <button 
                        style={btnSmallRed}
                        onClick={() => handleDelete(cat._id)}
                        aria-label={`Eliminar ${cat.category}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Estilos
const btnNav = {
  background: '#007bff',
  color: 'white',
  border: 'none',
  padding: '8px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnGreen = {
  background: '#28a745',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '14px',
};

const btnSmallGreen = {
  background: '#28a745',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const btnSmallBlue = {
  background: '#007bff',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const btnSmallRed = {
  background: '#dc3545',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const btnSmallGray = {
  background: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '6px 12px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

const inputStyle = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '14px',
  flex: 1,
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  borderBottom: '2px solid #dee2e6',
};

const tdStyle = {
  padding: '12px',
};

export default CategoriesManagement;
